import { Asset, AssetClass } from '@/types/api'

/**
 * Extended Asset interface with classification metadata
 * Includes fields for user override tracking and proxy information
 */
export interface AssetWithClassification extends Asset {
  /** Current classification (may be user-overridden) */
  assetClass: AssetClass

  /** Rules-based default classification (before any user overrides) */
  rulesBasedClass: AssetClass

  /** True if user has overridden the rules-based classification */
  isOverride: boolean

  /** Proxy information for assets with truncated histories */
  proxyInfo?: ProxyInfo

  /** Additional asset metadata */
  metadata: AssetMetadata

  /** ISO date string when asset was first listed (for "new" highlighting) */
  listingDate?: string

  /** Number of positions holding this asset (for portfolio impact warnings) */
  positionCount?: number
}

/**
 * Proxy information for assets using synthetic historical data
 * Per UI-06: Proxy Audit requirement
 */
export interface ProxyInfo {
  /** Ticker symbol of the proxy asset */
  proxyTicker: string

  /** Beta coefficient for proxy correlation */
  overlapBeta: number

  /** Time period for beta calculation (e.g., "2021-2022") */
  overlapPeriod: string

  /** Percentage of synthetic data vs real data (0-100) */
  percentSynthetic: number
}

/**
 * Asset metadata for display and analysis
 */
export interface AssetMetadata {
  /** Industry sector (e.g., "Technology", "DeFi") */
  sector?: string

  /** Market capitalization category */
  marketCap?: string

  /** ISO date string when asset was first listed */
  listingDate?: string

  /** Brief description of the asset */
  description?: string

  /** Asset category/subcategory */
  category?: string
}

/**
 * Request body for updating asset classification
 */
export interface UpdateClassificationRequest {
  /** New classification value */
  assetClass: AssetClass

  /** Whether this is an explicit override of rules-based default */
  isOverride: boolean
}

/**
 * Response from classification update
 */
export interface UpdateClassificationResponse {
  /** The updated asset with new classification */
  asset: AssetWithClassification

  /** Previous classification value (for undo/redo) */
  previousClass: AssetClass

  /** Whether the update was applied successfully */
  success: boolean

  /** Optional message if there were warnings */
  message?: string
}

/**
 * Filter options for asset list views
 */
export type AssetFilterType =
  | 'all'
  | 'crypto'
  | 'tradfi'
  | 'overrides'
  | 'new'

/**
 * Statistics for the asset classification dashboard
 */
export interface AssetClassificationStats {
  /** Total number of assets */
  total: number

  /** Number of CRYPTO assets */
  cryptoCount: number

  /** Number of TRADFI assets */
  tradfiCount: number

  /** Number of user-overridden classifications */
  overrideCount: number

  /** Number of newly listed assets (for review) */
  newCount: number

  /** Assets with positions (affected by classification changes) */
  assetsWithPositions: number
}

/**
 * Helper type for classification change events
 */
export interface ClassificationChangeEvent {
  /** Asset ID */
  assetId: string

  /** Previous classification */
  from: AssetClass

  /** New classification */
  to: AssetClass

  /** Whether this is an override */
  isOverride: boolean
}

/**
 * Check if an asset is "new" (listed within last 30 days)
 * @param listingDate - ISO date string
 * @returns true if asset is considered new
 */
export function isNewAsset(listingDate?: string): boolean {
  if (!listingDate) return false

  const listing = new Date(listingDate)
  const now = new Date()
  const daysSinceListing = (now.getTime() - listing.getTime()) / (1000 * 60 * 60 * 24)

  return daysSinceListing <= 30
}

/**
 * Get display label for asset class
 */
export function getAssetClassLabel(assetClass: AssetClass): string {
  return assetClass === 'CRYPTO' ? 'Crypto' : 'TradFi'
}

/**
 * Get color theme for asset class
 * Returns Tailwind color class names
 */
export function getAssetClassTheme(assetClass: AssetClass): {
  bg: string
  text: string
  border: string
  badge: string
} {
  if (assetClass === 'CRYPTO') {
    return {
      bg: 'bg-purple-50',
      text: 'text-purple-700',
      border: 'border-purple-200',
      badge: 'bg-purple-100 text-purple-800',
    }
  }

  return {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-800',
  }
}

/**
 * Type guard for AssetWithClassification
 */
export function isAssetWithClassification(
  asset: Asset | AssetWithClassification
): asset is AssetWithClassification {
  return (
    'rulesBasedClass' in asset &&
    'isOverride' in asset &&
    typeof asset.isOverride === 'boolean'
  )
}
